import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CommandLineIcon,
  ServerIcon,
  CurrencyDollarIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

/**
 * StatsWidget component displays real-time statistics about the Overseer bot
 * It fetches data from the stats worker API and displays it in a grid
 */
function StatsWidget() {
  const [stats, setStats] = useState({
    totalCommands: 0,
    serverCount: 0,
    totalCoinflips: 0,
    totalLostCoins: 0,
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Fetch stats from the worker API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        // Use the production URL or fallback to localhost for development
        const apiUrl = process.env.NODE_ENV === 'production'
          ? 'https://overseer-stats-worker.wrappeddeveloper.workers.dev'
          : 'http://localhost:8787';
        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error(`API responded with status: ${response.status}`);
        }

        const data = await response.json();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load stats. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchStats();

    // Set up polling every 30 seconds to keep stats updated
    const intervalId = setInterval(fetchStats, 30000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Stats items configuration
  const statsItems = [
    {
      icon: CommandLineIcon,
      title: "Commands Used",
      value: stats.totalCommands,
      color: "bg-blue-500"
    },
    {
      icon: ServerIcon,
      title: "Active Servers",
      value: stats.serverCount,
      color: "bg-green-500"
    },
    {
      icon: CurrencyDollarIcon,
      title: "Coinflips Done",
      value: stats.totalCoinflips,
      color: "bg-yellow-500"
    },
    {
      icon: ArrowTrendingDownIcon,
      title: "Coins Lost",
      value: stats.totalLostCoins,
      color: "bg-red-500"
    },
    {
      icon: UserGroupIcon,
      title: "Total Users",
      value: stats.totalUsers,
      color: "bg-purple-500"
    }
  ];

  // Loading state
  if (loading && stats.totalCommands === 0) {
    return (
      <div className="card p-6 mb-16">
        <h2 className="text-2xl font-semibold text-gray-custom-100 mb-4">Bot Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 rounded-lg bg-gray-custom-800 animate-pulse">
              <div className="h-10 w-10 rounded-full bg-gray-custom-700 mb-4"></div>
              <div className="h-4 w-24 bg-gray-custom-700 mb-2"></div>
              <div className="h-8 w-16 bg-gray-custom-700"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card p-6 mb-16">
        <h2 className="text-2xl font-semibold text-gray-custom-100 mb-4">Bot Statistics</h2>
        <div className="bg-red-900/30 border border-red-800 text-red-200 p-4 rounded-lg">
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-red-800 hover:bg-red-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render stats grid
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="card p-6 mb-16"
    >
      <h2 className="text-2xl font-semibold text-gray-custom-100 mb-4">Live Bot Statistics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statsItems.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
            className="p-6 rounded-lg bg-gray-custom-800 border border-gray-custom-700"
          >
            <div className={`p-3 rounded-full ${item.color} w-fit mb-4`}>
              <item.icon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-lg text-gray-custom-400 mb-1">{item.title}</h3>
            <p className="text-3xl font-bold text-gray-custom-100">{formatNumber(item.value)}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export default StatsWidget;
